// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { BrandDto } from "@insite/client-framework/Types/ApiModels";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    brand: BrandDto;
    /**
     * If true, the small brand logo will be displayed.
     * If false, the brand name will be displayed instead.
     * Default value: false */
    showLogo?: boolean;
    extendedStyles?: ProductBrandStyles;
    // XNG-Change
    redirectToPlp?: boolean;
    isLink?: boolean;
}

type Props = OwnProps;

export interface ProductBrandStyles {
    logoImage?: LazyImageProps;
    nameLink?: LinkPresentationProps;
    nameText?: TypographyProps;
}

const nameText: TypographyProps = {
    css: css`
        width: 100%;
        ${wrapInContainerStyles}
    `,
    size: "13px",
    weight: 600,
};

export const productBrandStyles: ProductBrandStyles = {
    nameText,
    nameLink: {
        css: css`
            width: 100%;
        `,
        typographyProps: nameText,
    },
};

const ProductBrand: FC<Props> = ({
    brand,
    showLogo = false,
    extendedStyles,
    // XNG-Change
    redirectToPlp = false,
    isLink = false,
}) => {
    if (!brand) {
        return null;
    }

    const [styles] = React.useState(() => mergeToNew(productBrandStyles, extendedStyles));

    let brandDisplay;
    if (showLogo && brand.logoSmallImagePath) {
        const altText = brand.logoImageAltText || brand.name;
        brandDisplay = <LazyImage {...styles.logoImage} src={brand.logoSmallImagePath} altText={altText} />;
    } else {
        brandDisplay =
            // XNG-Change
            brand.detailPagePath && isLink ? (
                brand.name
            ) : (
                <Typography {...styles.nameText} data-test-selector="brandName">
                    {brand.name}
                </Typography>
            );
    }

    // XNG-Change
    return brand.detailPagePath && isLink ? (
        <Link
            {...styles.nameLink}
            // XNG-Change
            href={redirectToPlp ? `${brand.detailPagePath}/Catalog/All` : brand.detailPagePath}
            data-test-selector="brandLink"
        >
            {brandDisplay}
        </Link>
    ) : (
        <>{brandDisplay}</>
    );
};

export default ProductBrand;
